import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

interface Project {
  id: string;
  title_en: string;
  category: string;
  status: string;
  location: string;
  beneficiaries: number;
  progress: number;
  created_at: string;
}

const CATEGORIES = ['Education', 'Healthcare', 'Infrastructure', 'Emergency Relief', 'Women Empowerment'];
const STATUSES = ['active', 'completed', 'paused'];

const emptyForm = {
  title_en: '', title_fr: '', title_de: '', title_ps: '', title_da: '',
  description_en: '', description_ps: '', description_da: '',
  category: 'Education', status: 'active', image_url: '',
  location: '', beneficiaries: 0, progress: 0,
};

export default function AdminProjectsPage() {
  const { isEditor } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchProjects = async () => {
    const { data } = await supabase.from('projects').select('id, title_en, category, status, location, beneficiaries, progress, created_at').order('created_at', { ascending: false });
    setProjects(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const openEdit = (p: Project) => {
    setForm({ ...emptyForm, title_en: p.title_en, category: p.category, status: p.status, location: p.location ?? '', beneficiaries: p.beneficiaries ?? 0, progress: p.progress ?? 0 });
    setEditId(p.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title_en.trim()) return;
    setSaving(true);
    if (editId) {
      await supabase.from('projects').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editId);
    } else {
      await supabase.from('projects').insert([form]);
    }
    setSaving(false);
    setShowModal(false);
    fetchProjects();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from('projects').delete().eq('id', deleteId);
    setDeleteId(null);
    fetchProjects();
  };

  const statusColor = (s: string) =>
    s === 'active' ? 'bg-green-100 text-green-700' : s === 'completed' ? 'bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'bg-gray-100 text-gray-500';

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B2838]">Projects</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage CAF projects and initiatives</p>
          </div>
          {isEditor && (
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] transition-colors whitespace-nowrap">
              <i className="ri-add-line"></i> Add Project
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-8 h-8 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              Loading projects...
            </div>
          ) : projects.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-folder-open-line text-3xl"></i></div>
              <p className="text-sm">No projects yet. Add your first project!</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Beneficiaries</th>
                  {isEditor && <th className="px-5 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {projects.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-[#1B2838]">{p.title_en}</div>
                      <div className="text-xs text-gray-400">{p.location}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{p.category}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(p.status)}`}>{p.status}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden w-20">
                          <div className="h-full bg-[#4ECDC4] rounded-full" style={{ width: `${p.progress}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500">{p.progress}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{p.beneficiaries?.toLocaleString()}</td>
                    {isEditor && (
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => openEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => setDeleteId(p.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#1B2838]">{editId ? 'Edit Project' : 'Add New Project'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (English) *</label>
                  <input value={form.title_en} onChange={e => setForm({ ...form, title_en: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" placeholder="Project title in English" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (French)</label>
                  <input value={form.title_fr} onChange={e => setForm({ ...form, title_fr: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" placeholder="Titre en français" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (German)</label>
                  <input value={form.title_de} onChange={e => setForm({ ...form, title_de: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" placeholder="Titel auf Deutsch" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] cursor-pointer">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] cursor-pointer">
                    {STATUSES.map(s => <option key={s} className="capitalize">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Location</label>
                  <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" placeholder="e.g. Kabul, Afghanistan" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Beneficiaries</label>
                  <input type="number" value={form.beneficiaries} onChange={e => setForm({ ...form, beneficiaries: Number(e.target.value) })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" min={0} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Progress (%)</label>
                  <input type="number" value={form.progress} onChange={e => setForm({ ...form, progress: Math.min(100, Math.max(0, Number(e.target.value))) })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" min={0} max={100} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image URL</label>
                  <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]" placeholder="https://..." />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description (English)</label>
                  <textarea value={form.description_en} onChange={e => setForm({ ...form, description_en: e.target.value })} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none" placeholder="Project description..." />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.title_en.trim()} className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2">
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                {editId ? 'Save Changes' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1B2838] text-center mb-2">Delete Project?</h3>
            <p className="text-sm text-gray-500 text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-bold cursor-pointer hover:bg-red-600 whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
